/**
 * Security+ question bank
 * Schema: { id, domain, subdomain, stem, choices: string[], answer: number, explanation: string, difficulty }
 */
const SEC = [
  {
    id: "SEC-001",
    domain: "Security+",
    subdomain: "Foundations",
    stem: "Which principle of the CIA triad ensures information is not altered by unauthorized parties?",
    choices: ["Confidentiality", "Integrity", "Availability", "Non-repudiation"],
    answer: 1,
    explanation: "Integrity protects against unauthorized modification of data. Hashing, digital signatures, and file integrity monitoring are common integrity controls, ensuring data remains accurate and trustworthy from creation to consumption.",
    difficulty: "easy"
  },
  {
    id: "SEC-002",
    domain: "Security+",
    subdomain: "Risk Management",
    stem: "A company purchases cyber insurance to address potential breach costs. Which risk response is this?",
    choices: ["Avoid", "Transfer", "Mitigate", "Accept"],
    answer: 1,
    explanation: "Risk transfer shifts financial impact to a third party (e.g., insurer). It does not reduce likelihood; instead it offsets potential losses, often used alongside mitigation controls for a balanced strategy.",
    difficulty: "easy"
  },
  {
    id: "SEC-003",
    domain: "Security+",
    subdomain: "Authentication",
    stem: "Which is the strongest MFA combination?",
    choices: ["Password + SMS OTP", "Password + App-based TOTP", "Password + Security key (FIDO2)", "Two passwords"],
    answer: 2,
    explanation: "Hardware-backed FIDO2/WebAuthn keys provide phishing-resistant authentication with asymmetric cryptography and origin binding, outperforming SMS and app-based codes that are vulnerable to interception or social engineering.",
    difficulty: "medium"
  },
  {
    id: "SEC-004",
    domain: "Security+",
    subdomain: "PKI & Crypto",
    stem: "What is the primary purpose of a digital certificate in TLS?",
    choices: ["Encrypt the data at rest", "Provide non-repudiation only", "Bind a public key to an identity", "Store session keys"],
    answer: 2,
    explanation: "X.509 certificates bind a subject’s identity (e.g., domain name) to a public key, signed by a trusted CA. Clients validate this chain to prevent impersonation and enable secure key exchange.",
    difficulty: "easy"
  },
  {
    id: "SEC-005",
    domain: "Security+",
    subdomain: "PKI & Crypto",
    stem: "Which statement best describes asymmetric encryption?",
    choices: ["Same key for encrypt and decrypt", "Different keys for encrypt and decrypt", "Hashes with a salt", "Stream cipher only"],
    answer: 1,
    explanation: "Asymmetric crypto uses a key pair: a public key (encrypt/verify) and a private key (decrypt/sign). It enables key exchange and digital signatures, often combined with symmetric ciphers for bulk data.",
    difficulty: "easy"
  },
  {
    id: "SEC-006",
    domain: "Security+",
    subdomain: "Secure Protocols",
    stem: "Which protocol combination secures web traffic with modern best practice?",
    choices: ["HTTP + MD5", "TLS 1.2/1.3 + HTTPS", "SSL 3.0 + HTTPS", "WEP + HTTPS"],
    answer: 1,
    explanation: "HTTPS over TLS 1.2 or 1.3 provides confidentiality, integrity, and authentication. Deprecated SSL and weak hashes like MD5 are insecure and should not be used.",
    difficulty: "easy"
  },
  {
    id: "SEC-007",
    domain: "Security+",
    subdomain: "Identity",
    stem: "Which access control model assigns permissions based on a user’s job function?",
    choices: ["Discretionary (DAC)", "Mandatory (MAC)", "Role-Based (RBAC)", "Rule-Based (RB-RBAC only)"],
    answer: 2,
    explanation: "RBAC ties permissions to roles like 'HR Analyst' or 'DB Admin.' Users inherit permissions through role membership, reducing privilege sprawl and simplifying provisioning and audits.",
    difficulty: "easy"
  },
  {
    id: "SEC-008",
    domain: "Security+",
    subdomain: "Identity",
    stem: "Kerberos primarily provides which capability in an enterprise domain?",
    choices: ["Passwordless email", "Single sign-on with tickets", "VPN tunneling", "Public CA issuance"],
    answer: 1,
    explanation: "Kerberos uses a KDC to issue time-bound tickets, allowing single sign-on to multiple services. Mutual authentication and short-lived tickets reduce credential exposure and replay risk.",
    difficulty: "medium"
  },
  {
    id: "SEC-009",
    domain: "Security+",
    subdomain: "Network Security",
    stem: "A reverse proxy in front of web servers is commonly used for:",
    choices: ["Client-side encryption only", "Load balancing, TLS offload, and WAF inspection", "Replacing routers", "NAT traversal"],
    answer: 1,
    explanation: "Reverse proxies terminate client connections and can offload TLS, distribute traffic, cache content, and host WAF rules to block attacks before they reach origin servers.",
    difficulty: "medium"
  },
  {
    id: "SEC-010",
    domain: "Security+",
    subdomain: "Network Security",
    stem: "802.1X with EAP-TLS provides strong port-based access control by requiring:",
    choices: ["Simple PSK", "Client and server certificates", "MAC filtering only", "Open authentication"],
    answer: 1,
    explanation: "EAP-TLS uses mutual certificate-based auth via RADIUS. Devices prove possession of a private key, stopping many credential theft and spoofing techniques common on wired/wireless networks.",
    difficulty: "medium"
  },
  {
    id: "SEC-011",
    domain: "Security+",
    subdomain: "Wireless",
    stem: "Which Wi-Fi security option is recommended for modern deployments?",
    choices: ["WEP", "WPA (TKIP)", "WPA2 (AES-CCMP)", "WPA3 (SAE)"],
    answer: 3,
    explanation: "WPA3-SAE brings forward secrecy and stronger handshake properties. If WPA3 isn’t fully supported, WPA2 with AES-CCMP remains acceptable; avoid WEP and TKIP.",
    difficulty: "easy"
  },
  {
    id: "SEC-012",
    domain: "Security+",
    subdomain: "Threats",
    stem: "Which attack tricks users into executing code in their browser on a trusted site?",
    choices: ["SQL Injection", "Stored Cross-Site Scripting (XSS)", "CSRF", "DNS poisoning"],
    answer: 1,
    explanation: "Stored XSS persists malicious scripts on a site, executing in victims’ browsers. Output encoding and CSP reduce impact; input validation helps but is not sufficient alone.",
    difficulty: "medium"
  },
  {
    id: "SEC-013",
    domain: "Security+",
    subdomain: "Threats",
    stem: "CSRF primarily exploits which weakness?",
    choices: ["Lack of input validation", "Trust in a user’s authenticated session", "Broken TLS", "Weak hashing"],
    answer: 1,
    explanation: "CSRF abuses a valid user session to trigger unwanted actions. Mitigations include same-site cookies, anti-CSRF tokens, and requiring re-auth or step-up for sensitive actions.",
    difficulty: "medium"
  },
  {
    id: "SEC-014",
    domain: "Security+",
    subdomain: "Malware",
    stem: "A RAT (Remote Access Trojan) most commonly provides:",
    choices: ["Local privilege only", "Persistent covert remote control", "Fileless macro scanning", "Only keylogging"],
    answer: 1,
    explanation: "RATs establish command channels for persistent remote control, often using living-off-the-land tools to blend in. EDR, isolation, and C2 traffic detection are key defenses.",
    difficulty: "medium"
  },
  {
    id: "SEC-015",
    domain: "Security+",
    subdomain: "Crypto",
    stem: "Which option best matches the function of a hash?",
    choices: ["Reversible encryption", "Fixed-length, one-way digest", "Key exchange", "Bulk data cipher"],
    answer: 1,
    explanation: "Hashes produce fixed-size outputs and are designed to be one-way. With salts and slow KDFs, they help protect stored credentials from brute-force and rainbow-table attacks.",
    difficulty: "easy"
  },
  {
    id: "SEC-016",
    domain: "Security+",
    subdomain: "PKI",
    stem: "OCSP stapling improves TLS performance and privacy by:",
    choices: ["Eliminating certificates", "Embedding a CA private key in servers", "Letting servers present a recent OCSP response", "Requiring CRLs on clients"],
    answer: 2,
    explanation: "Servers 'staple' a signed OCSP status from the CA to the handshake, avoiding client lookups that leak browsing patterns and reducing latency while preserving revocation checks.",
    difficulty: "medium"
  },
  {
    id: "SEC-017",
    domain: "Security+",
    subdomain: "Identity",
    stem: "TACACS+ typically uses which port and characteristic compared to RADIUS?",
    choices: ["UDP/1812; combined auth+acct", "TCP/49; separates auth and authorization", "UDP/49; no encryption", "TCP/88; ticket-based"],
    answer: 1,
    explanation: "TACACS+ uses TCP 49 and encrypts the entire payload. It separates authentication and authorization steps, popular for network device admin access control.",
    difficulty: "medium"
  },
  {
    id: "SEC-018",
    domain: "Security+",
    subdomain: "Identity",
    stem: "LDAP over TLS typically uses which default port?",
    choices: ["389", "443", "636", "3268"],
    answer: 2,
    explanation: "LDAPS commonly runs on TCP 636. Plain LDAP on 389 can be upgraded with STARTTLS, but dedicated LDAPS simplifies secure-by-default configurations.",
    difficulty: "easy"
  },
  {
    id: "SEC-019",
    domain: "Security+",
    subdomain: "Secure Protocols",
    stem: "Which version is considered secure today?",
    choices: ["SSL 3.0", "TLS 1.0", "TLS 1.2/1.3", "WEP"],
    answer: 2,
    explanation: "TLS 1.2 and 1.3 are modern standards. Older SSL/TLS versions are vulnerable (e.g., POODLE, BEAST), and WEP is unrelated and insecure for Wi-Fi.",
    difficulty: "easy"
  },
  {
    id: "SEC-020",
    domain: "Security+",
    subdomain: "Logging & Monitoring",
    stem: "A SIEM primarily provides:",
    choices: ["Long-term backups", "Event aggregation, correlation, and alerting", "Packet routing", "Code compilation"],
    answer: 1,
    explanation: "SIEMs ingest logs, normalize formats, correlate events, and raise alerts. With SOAR integrations, they can automate response actions to reduce MTTR.",
    difficulty: "easy"
  },
  {
    id: "SEC-021",
    domain: "Security+",
    subdomain: "Incident Response",
    stem: "Which is the correct high-level IR sequence?",
    choices: ["Eradication → Containment → Recovery", "Preparation → Identification → Containment → Eradication → Recovery → Lessons Learned", "Detection → Backups → Wipe → Report", "Containment → Ignore → Recover"],
    answer: 1,
    explanation: "Standard IR lifecycle: prepare, identify, contain, eradicate, recover, and document lessons learned to improve controls and playbooks for future incidents.",
    difficulty: "medium"
  },
  {
    id: "SEC-022",
    domain: "Security+",
    subdomain: "Forensics",
    stem: "Chain of custody documentation ensures:",
    choices: ["Data encryption at rest", "Proper evidence handling and admissibility", "Zero trust in the lab", "Patch verification"],
    answer: 1,
    explanation: "Chain of custody tracks who handled evidence, when, and how. This preserves integrity and supports admissibility by preventing tampering claims.",
    difficulty: "easy"
  },
  {
    id: "SEC-023",
    domain: "Security+",
    subdomain: "BC/DR",
    stem: "Which backup strategy captures only the changes since the last backup of any type?",
    choices: ["Full", "Differential", "Incremental", "Synthetic full"],
    answer: 2,
    explanation: "Incremental backups store changes since the last full or incremental, minimizing backup time and storage. Restores require the last full plus all subsequent incrementals.",
    difficulty: "medium"
  },
  {
    id: "SEC-024",
    domain: "Security+",
    subdomain: "Policies",
    stem: "A standard vs. a policy is best described as:",
    choices: ["Policy is optional; standard is mandatory", "Policy states 'what/why'; standards/procedures detail 'how/when'", "Both are legal contracts", "Standards are only for networking"],
    answer: 1,
    explanation: "Policies are high-level directives backed by management. Standards and procedures define specific technical requirements and steps to implement the policy consistently.",
    difficulty: "easy"
  },
  {
    id: "SEC-025",
    domain: "Security+",
    subdomain: "Cloud",
    stem: "You manage application code and data but not the underlying OS. Which cloud model is this?",
    choices: ["IaaS", "PaaS", "SaaS", "FaaS only"],
    answer: 1,
    explanation: "PaaS abstracts servers and OS management. You deploy code and configure runtimes; the provider handles patching, scaling, and platform upkeep.",
    difficulty: "easy"
  },
  {
    id: "SEC-026",
    domain: "Security+",
    subdomain: "Cloud",
    stem: "A CASB primarily provides what benefit?",
    choices: ["Email encryption", "Governance and visibility for cloud apps", "Only DLP for endpoints", "VPN replacement"],
    answer: 1,
    explanation: "Cloud Access Security Brokers enforce policies such as DLP, access control, and threat protection across sanctioned and unsanctioned cloud services with API or proxy modes.",
    difficulty: "medium"
  },
  {
    id: "SEC-027",
    domain: "Security+",
    subdomain: "DevSecOps",
    stem: "SAST vs DAST is best described as:",
    choices: ["SAST tests running apps; DAST reads source", "SAST analyzes source/binaries; DAST tests running apps externally", "Both are the same", "Neither finds injection flaws"],
    answer: 1,
    explanation: "SAST examines code/bytecode pre-runtime; DAST probes a live application from the outside. Combined with SCA and IaC scanning, they reduce vulnerabilities earlier in the SDLC.",
    difficulty: "medium"
  },
  {
    id: "SEC-028",
    domain: "Security+",
    subdomain: "Secure Coding",
    stem: "Which control best mitigates SQL injection?",
    choices: ["Input length checks only", "Parameterized queries/prepared statements", "Client-side filtering", "Obscure error messages"],
    answer: 1,
    explanation: "Parameterized queries separate code from data, preventing attacker-controlled input from being executed. ORM frameworks and stored procedures can help when parameterized correctly.",
    difficulty: "easy"
  },
  {
    id: "SEC-029",
    domain: "Security+",
    subdomain: "Endpoint",
    stem: "Application allow-listing primarily helps prevent:",
    choices: ["Phishing emails", "Execution of unauthorized binaries/scripts", "DNS tunneling", "Wi-Fi deauth"],
    answer: 1,
    explanation: "Allow-listing permits only approved executables to run, stopping many malware strains and LOLBins abuse. It’s powerful when paired with code signing and least privilege.",
    difficulty: "medium"
  },
  {
    id: "SEC-030",
    domain: "Security+",
    subdomain: "Endpoint",
    stem: "MDM with containerization on BYOD is used to:",
    choices: ["Encrypt the entire phone", "Separate corporate data/apps from personal space", "Block all personal apps", "Root the device"],
    answer: 1,
    explanation: "Containerization isolates corporate apps/data with managed policies (DLP, remote wipe) without taking over personal areas, balancing security and privacy on BYOD.",
    difficulty: "easy"
  },
  {
    id: "SEC-031",
    domain: "Security+",
    subdomain: "Vulnerability Mgmt",
    stem: "Which order aligns with a typical vuln management cycle?",
    choices: ["Remediate → Scan → Prioritize → Report", "Scan → Validate → Prioritize → Remediate → Verify", "Report → Ignore → Repeat", "Patch only critical CVEs"],
    answer: 1,
    explanation: "You scan, validate findings to remove noise, prioritize by risk (CVSS + exploitability + asset value), remediate, then verify fixes with rescans and reporting.",
    difficulty: "medium"
  },
  {
    id: "SEC-032",
    domain: "Security+",
    subdomain: "Pen Testing",
    stem: "A black-box penetration test means the tester has:",
    choices: ["Full internal knowledge", "Limited knowledge (e.g., URLs) only", "No authorization", "Regulatory immunity"],
    answer: 1,
    explanation: "Black-box simulates an external attacker with minimal information. Gray-box has partial knowledge; white-box has full knowledge including architecture and source code.",
    difficulty: "medium"
  },
  {
    id: "SEC-033",
    domain: "Security+",
    subdomain: "Web Security",
    stem: "A secure cookie directive that prevents access via client-side scripts is:",
    choices: ["Secure", "Path=/", "HttpOnly", "Max-Age"],
    answer: 2,
    explanation: "HttpOnly prevents JavaScript from reading cookie values, reducing theft via XSS. Use with Secure (TLS only) and SameSite to reduce CSRF and session hijacking risks.",
    difficulty: "easy"
  },
  {
    id: "SEC-034",
    domain: "Security+",
    subdomain: "Web Security",
    stem: "Content Security Policy (CSP) is designed to mitigate:",
    choices: ["CSRF only", "XSS and content injection by restricting sources", "SQL injection", "Password spraying"],
    answer: 1,
    explanation: "CSP limits where scripts, images, and other resources can load from. It reduces XSS impact by disallowing inline scripts and untrusted origins.",
    difficulty: "medium"
  },
  {
    id: "SEC-035",
    domain: "Security+",
    subdomain: "Wireless",
    stem: "Enterprise Wi-Fi using PEAP-MSCHAPv2 is vulnerable if:",
    choices: ["Certificates are validated", "Users accept rogue RADIUS cert prompts", "TLS 1.3 is enforced", "EAP-TLS is used"],
    answer: 1,
    explanation: "If users click through bad certificates, attackers can run evil-twin APs and capture credentials. Enforce server cert validation or prefer EAP-TLS for stronger protection.",
    difficulty: "medium"
  },
  {
    id: "SEC-036",
    domain: "Security+",
    subdomain: "Secure Design",
    stem: "Which architecture pattern limits lateral movement by dividing networks into zones?",
    choices: ["Flat network", "Microsegmentation/segmentation", "Hub-and-spoke switching", "NAT64"],
    answer: 1,
    explanation: "Segmentation and microsegmentation apply granular controls between zones/workloads. This reduces blast radius when a host is compromised.",
    difficulty: "medium"
  },
  {
    id: "SEC-037",
    domain: "Security+",
    subdomain: "Monitoring",
    stem: "Anomaly-based IDS primarily detects:",
    choices: ["Only signatures", "Deviations from a learned baseline", "Routing loops", "SSL offload events"],
    answer: 1,
    explanation: "Anomaly IDS tools learn normal behavior and alert on deviations, catching novel attacks that signature systems may miss. Tuning is needed to reduce false positives.",
    difficulty: "medium"
  },
  {
    id: "SEC-038",
    domain: "Security+",
    subdomain: "Physical",
    stem: "Mantraps and badge readers primarily mitigate:",
    choices: ["Phishing", "Tailgating and unauthorized entry", "SQL injection", "RF jamming"],
    answer: 1,
    explanation: "Physical access controls like mantraps enforce one-at-a-time entry and require valid credentials, reducing tailgating and social engineering at doors.",
    difficulty: "easy"
  },
  {
    id: "SEC-039",
    domain: "Security+",
    subdomain: "OT/ICS",
    stem: "Why are ICS/SCADA networks often placed in separate zones with strict controls?",
    choices: ["They are immune to attacks", "They require internet for availability", "Safety and uptime risks demand isolation and strict change control", "They only use TLS 1.3"],
    answer: 2,
    explanation: "Industrial systems control physical processes. Compromise can cause safety incidents and downtime, so segmentation, allowlisting, and strict change procedures are critical.",
    difficulty: "medium"
  },
  {
    id: "SEC-040",
    domain: "Security+",
    subdomain: "Email Security",
    stem: "Which trio helps validate sending domains and reduce spoofing?",
    choices: ["SPF, DKIM, DMARC", "TLS, IPSec, SSH", "NAT, PAT, ACL", "PEAP, EAP-TLS, PSK"],
    answer: 0,
    explanation: "SPF defines authorized senders; DKIM signs mail; DMARC sets alignment and enforcement policy. Together they reduce spoofing and improve deliverability.",
    difficulty: "easy"
  },
  {
    id: "SEC-041",
    domain: "Security+",
    subdomain: "Data Security",
    stem: "Tokenization differs from encryption by:",
    choices: ["Being reversible with a key", "Replacing sensitive data with a mapped token stored separately", "Hashing values with salt", "Compressing data"],
    answer: 1,
    explanation: "Tokenization replaces sensitive elements (e.g., PAN) with tokens. The mapping resides in a secure vault, limiting exposure across systems without widespread decryption.",
    difficulty: "medium"
  },
  {
    id: "SEC-042",
    domain: "Security+",
    subdomain: "Privacy",
    stem: "Data minimization in privacy programs means:",
    choices: ["Collecting as much as possible", "Collecting only what is necessary for the stated purpose", "Encrypting all data", "Deleting all logs"],
    answer: 1,
    explanation: "Collect only required data, keep it only as long as needed, and protect it appropriately. Minimization reduces breach impact and regulatory exposure.",
    difficulty: "easy"
  },
  {
    id: "SEC-043",
    domain: "Security+",
    subdomain: "Threats",
    stem: "A watering-hole attack targets:",
    choices: ["Any random website", "Sites frequently visited by the victim group", "Only mobile apps", "VPN concentrators"],
    answer: 1,
    explanation: "Attackers compromise a legitimate site popular with a target group, then deliver malware or exploits to visiting users to infiltrate the organization.",
    difficulty: "medium"
  },
  {
    id: "SEC-044",
    domain: "Security+",
    subdomain: "Threats",
    stem: "Which social engineering technique relies on voice calls?",
    choices: ["Smishing", "Vishing", "Pharming", "Pretexting cannot use voice"],
    answer: 1,
    explanation: "Vishing uses voice (phone/VoIP) to manipulate victims into revealing information or taking actions. Often combined with spoofed caller ID and urgent pretexts.",
    difficulty: "easy"
  },
  {
    id: "SEC-045",
    domain: "Security+",
    subdomain: "Host Security",
    stem: "HIPS/EDR tools are primarily designed to:",
    choices: ["Replace patching", "Provide host-level detection/response, behavioral analytics, and isolation", "Tune switches", "Encrypt Wi-Fi"],
    answer: 1,
    explanation: "EDR continuously monitors endpoints, detects suspicious behaviors, and can auto-contain hosts. It complements prevention tools and centralizes telemetry for investigations.",
    difficulty: "medium"
  },
  {
    id: "SEC-046",
    domain: "Security+",
    subdomain: "Patching",
    stem: "A maintenance window is used to:",
    choices: ["Delay all patches", "Plan and communicate change times to reduce impact", "Avoid change control", "Bypass rollback plans"],
    answer: 1,
    explanation: "Maintenance windows coordinate changes, notify stakeholders, and ensure backup/rollback plans exist, reducing operational risk during updates.",
    difficulty: "easy"
  },
  {
    id: "SEC-047",
    domain: "Security+",
    subdomain: "Hardening",
    stem: "Disabling unused services on servers primarily reduces:",
    choices: ["Availability", "Attack surface", "Bandwidth", "Confidentiality only"],
    answer: 1,
    explanation: "Each unnecessary service may introduce vulnerabilities. Minimizing services and ports reduces exposure and eases monitoring and patching.",
    difficulty: "easy"
  },
  {
    id: "SEC-048",
    domain: "Security+",
    subdomain: "Monitoring",
    stem: "NetFlow/IPFIX data is MOST useful for:",
    choices: ["Full packet payload inspection", "Traffic metadata analysis (who/where/how much)", "File integrity checking", "Privileged access reviews"],
    answer: 1,
    explanation: "Flow records summarize conversations (src/dst, ports, bytes). They reveal anomalies, data exfiltration patterns, and service misuse without deep packet inspection.",
    difficulty: "medium"
  },
  {
    id: "SEC-049",
    domain: "Security+",
    subdomain: "Secure Protocols",
    stem: "Which ports are the default for RADIUS authentication/accounting?",
    choices: ["49/49", "1812/1813", "88/464", "389/3268"],
    answer: 1,
    explanation: "RADIUS commonly uses UDP 1812 (authentication/authorization) and 1813 (accounting). TACACS+ uses TCP 49; Kerberos uses TCP/UDP 88.",
    difficulty: "easy"
  },
  {
    id: "SEC-050",
    domain: "Security+",
    subdomain: "Governance",
    stem: "Security awareness training primarily aims to reduce:",
    choices: ["Insurance premiums only", "Human-driven risks like phishing and unsafe behaviors", "Patch cycle time", "Firewall load"],
    answer: 1,
    explanation: "Humans are frequent entry points. Regular, relevant training and simulated phishing improve reporting and reduce successful social engineering attacks.",
    difficulty: "easy"
  }
  ,
  {
    id: "SEC-051",
    domain: "Security+",
    subdomain: "Social Engineering",
    stem: "Which attack uses physical objects left in public to trick victims into plugging them into corporate machines?",
    choices: ["Phishing", "Baiting", "Pretexting", "Vishing"],
    answer: 1,
    explanation: "Baiting leaves malicious or tempting physical media (USB drives) to entice users to connect them, potentially delivering malware or exfiltration tools.",
    difficulty: "easy"
  },
  {
    id: "SEC-052",
    domain: "Security+",
    subdomain: "Access Control",
    stem: "Applying the principle of least privilege means:",
    choices: ["Giving all users admin rights for convenience", "Granting users only the access they need to perform their job", "Sharing one service account among team members", "Removing all access rights"],
    answer: 1,
    explanation: "Least privilege limits permissions to the minimum necessary, reducing the blast radius of compromised accounts or insider misuse.",
    difficulty: "easy"
  },
  {
    id: "SEC-053",
    domain: "Security+",
    subdomain: "Data Security",
    stem: "Which technology is primarily used to protect data at rest on a laptop in case the device is lost?",
    choices: ["Full-disk encryption (FDE)", "TLS", "OCSP", "DNSSEC"],
    answer: 0,
    explanation: "FDE (e.g., BitLocker, FileVault) encrypts the entire disk, protecting data if the physical device is stolen. TLS protects data in transit.",
    difficulty: "easy"
  },
  {
    id: "SEC-054",
    domain: "Security+",
    subdomain: "Network Security",
    stem: "A Web Application Firewall (WAF) primarily inspects traffic at which OSI layer?",
    choices: ["Layer 3 (Network)", "Layer 4 (Transport)", "Layer 7 (Application)", "Layer 2 (Data Link)"],
    answer: 2,
    explanation: "WAFs analyze HTTP/HTTPS payloads and application-layer requests to block injection and other web-focused attacks at Layer 7.",
    difficulty: "medium"
  },
  {
    id: "SEC-055",
    domain: "Security+",
    subdomain: "Supply Chain",
    stem: "A supply-chain compromise typically involves which scenario?",
    choices: ["Rewriting local binaries only", "Compromising a vendor or third-party component to push malicious code to many victims", "Only physical tampering of wiring", "DNS cache poisoning only"],
    answer: 1,
    explanation: "Supply-chain attacks target vendors or third-party libraries to distribute malicious changes widely (e.g., trojanized updates). Vendor vetting and code signing help mitigate this.",
    difficulty: "medium"
  },
  {
    id: "SEC-056",
    domain: "Security+",
    subdomain: "Mobile",
    stem: "Which approach balances corporate control and user privacy for BYOD devices?",
    choices: ["Full device wipe without notice", "Containerization of corporate apps and selective wipe", "Requiring device rooting", "Blocking all personal apps"],
    answer: 1,
    explanation: "Containerization separates corporate data and allows IT to selectively wipe corporate content without touching personal data, respecting privacy while enforcing security.",
    difficulty: "easy"
  },
  {
    id: "SEC-057",
    domain: "Security+",
    subdomain: "Detection",
    stem: "What is the primary purpose of a honeypot in network defense?",
    choices: ["Replace firewalls", "Attract and study attackers to gather intel and detect activity", "Provide production services", "Encrypt backups"],
    answer: 1,
    explanation: "Honeypots are decoy systems designed to lure attackers, enabling defenders to observe tactics, techniques, and procedures and improve detection.",
    difficulty: "medium"
  },
  {
    id: "SEC-058",
    domain: "Security+",
    subdomain: "Hardening",
    stem: "Using benchmarks like CIS or DISA STIGs primarily helps organizations to:",
    choices: ["Increase performance only", "Standardize secure configurations and reduce attack surface", "Avoid patching", "Guarantee zero breaches"],
    answer: 1,
    explanation: "Baseline hardening benchmarks provide prescriptive configuration guidance to consistently secure systems and reduce vulnerabilities; they don’t replace patching or monitoring.",
    difficulty: "easy"
  }
];

export default SEC;
